import React, { useEffect, useState } from 'react';
import CustomDialog, { ISideButton } from 'components/CustomDialog/CustomDialog';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import GridContainer from 'components/Grid/GridContainer';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch } from 'react-redux';
import { AccountsService } from 'services/accounts/accounts';
import { BillsService } from 'services/bills/bills';
import { finalize } from 'rxjs/operators';
import invalidateCache from 'utils/api/invalidateCache';
import { translate } from 'components/Translation';
import { setToast } from 'redux/modules/toast/actions';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { IBillingAccountInformation } from 'utils/api/models/accounts/billingAccountInformation';
import DialogBody from './subcomponents/DialogBody';
import EBills from './subcomponents/EBills';
import BillingAccountInformation from './subcomponents/BillingAccountInformation';
import BillingAccountDetailsHeader from './subcomponents/BillingAccountDetailsHeader';
import { TagManager } from 'commons/gtm/tagManager';
import { getLanguage } from 'utils/language/language';

interface IDetailsProps {
	match: {
		path: string;
		params: {
			subpage: string;
			billingId: string;
			accountId: string;
		};
	};
}

const BillingAccountDetails = (props: IDetailsProps) => {
	const { billingId, accountId } = props.match.params;
	const [billingAccountInformation, setBillingAccountInformation] = useState<IBillingAccountInformation | null>(null);
	const [eBillRecipients, setEBillRecipients] = useState<Array<string>>([]);
	const [account, setAccount] = useState<SingleCorpAccount | null>(null);
	const [isEBill, setIsEBill] = useState(false);
	const [newBillingName, setNewBillingName] = useState('');
	const [billingNameDialog, setBillingNameDialog] = useState(false);
	const [recipientDialog, setRecipientDialog] = useState(false);
	const [loadingBillingInformation, setLoadingBillingInformation] = useState(true);
	const [loadingEBillInformation, setLoadingEBillInformation] = useState(false);
	const [action, setAction] = useState('add');
	const [recipientEmail, setRecipientEmail] = useState('');
	const [recipientIndex, setRecipientIndex] = useState(0);
	const dispatch = useDispatch();

	const billingName = billingAccountInformation?.billingAccountName || translate('payments.billingAccounts.no-name');
	const show = !loadingBillingInformation && !loadingEBillInformation && !!billingAccountInformation;

	useEffect(() => {
		AccountsService.getCorpAccountsForCurrentUser().subscribe(({ accounts }) => {
			setAccount(accounts.find((account) => account.accountId === accountId) || null);
		});
	}, [accountId]);

	useEffect(() => {
		const subscription = AccountsService.getBillingAccountInformation({
			billingId,
			accountId,
		})
			.pipe(finalize(() => setLoadingBillingInformation(false)))
			.subscribe((response) => {
				setBillingAccountInformation(response);
				setNewBillingName(response.billingAccountName || translate('payments.billingAccounts.no-name'));
			});
		return () => {
			subscription.unsubscribe();
		};
	}, [billingId, accountId]);

	useEffect(() => {
		if (billingAccountInformation) {
			setLoadingEBillInformation(true);
			/**
			 * @description
			 * Updated logic for API check.
			 * In the old API, we validated the "activated" status to determine if the process was complete.
			 * In the new API, we now check the "statementToEmail" attribute to ensure that the length of its value is greater than 0.
			 *
			 * @note
			 * - The old implementation relied on checking the "activated" status to verify completion.
			 * - The new API uses a length-based validation to ensure that the "statementToEmail" field is not empty.
			 * - This change improves the robustness of data validation for email processing.
			 */

			BillsService.getEbillInfo(billingAccountInformation.billingId, ['statementToEmail'])
				.pipe(finalize(() => setLoadingEBillInformation(false)))
				.subscribe(({ response }) => {
					const data = response?.data?.billingAttributes;
					const statementToEmail = data.find(
						(statement: { attributeKey: string; attributeValues: string[] }) =>
							statement?.attributeKey === 'statementToEmail'
					);
					if (statementToEmail && statementToEmail?.attributeValues?.length > 0) {
						// we now check the "statementToEmail" attribute to ensure that the length of its value is greater than 0
						setIsEBill(true);
						const recipientsString = statementToEmail?.attributeValues;
						if (recipientsString.length > 0) {
							setEBillRecipients(
								recipientsString
									.filter((email: string) => email !== billingAccountInformation?.email)
									.filter(Boolean)
							);
						}
					}
				});
		}
	}, [billingAccountInformation]);

	useEffect(() => {
		if (!loadingEBillInformation && !loadingBillingInformation) {
			dispatch(hideLoadingIndicator());
		} else {
			dispatch(showLoadingIndicator());
		}
	}, [loadingBillingInformation, loadingEBillInformation, dispatch]);

	const hideUpdateAccountNameDialog = () => {
		setBillingNameDialog(false);
		setNewBillingName(billingName);
	};

	const updateBillingAccountInformation = () => {
		dispatch(showLoadingIndicator());
		AccountsService.updateBillingAccountInformation({ accountId, billingAccountName: newBillingName, billingId })
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(
				() => {
					invalidateCache(
						['getCorpBillingIds', 'getBillsHistoryForAccounts', 'getBillingAccountInformation'],
						true
					);
					if (!!billingAccountInformation && Object.keys(billingAccountInformation).length) {
						setBillingAccountInformation((info) =>
							info
								? {
										...info,
										billingAccountName: newBillingName,
								  }
								: null
						);
					}
				},
				() => {}
			);
	};

	const editRecipient = (index: number, email: string) => {
		setAction('edit');
		setRecipientEmail(email);
		setRecipientIndex(index);
		setRecipientDialog(true);
	};

	const addRecipient = () => {
		setAction('add');
		setRecipientEmail('');
		setRecipientIndex(eBillRecipients.length);
		setRecipientDialog(true);
	};

	const updateRecipients = () => {
		const emails: string[] = [...eBillRecipients];
		if (action === 'add') {
			emails.push(recipientEmail);
		} else if (action === 'edit') {
			emails[recipientIndex] = recipientEmail;
		}
		setRecipientDialog(false);
		dispatch(showLoadingIndicator());
		BillsService.updateEbillEmails({
			accountId,
			billAccountId: billingId,
			emails,
		})
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(({ response }) => {
				if (response.arguments.returnValue === 'true') {
					setEBillRecipients(emails);
					dispatch(
						setToast({
							error: false,
							label: translate('billing.account.ebills.updated'),
							actionType: translate('updated'),
						})
					);
				}
			});
	};

	const deleteRecipient = () => {
		setRecipientDialog(false);
		dispatch(showLoadingIndicator());
		const emails = [...eBillRecipients];
		emails.splice(recipientIndex, 1);
		BillsService.updateEbillEmails({
			accountId,
			billAccountId: billingId,
			emails,
		})
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(({ response }) => {
				if (response.arguments.returnValue === 'true') {
					setEBillRecipients(emails);
					dispatch(
						setToast({
							error: false,
							label: translate('billing.account.ebill.deleted'),
							actionType: translate('deleted'),
						})
					);
				}
			});
	};
	useEffect(() => {
		TagManager.dataLayer({
			dataLayer: {
				event: 'My Services',
				step: 'My Active Services:Account Details',
				accountID: accountId,
				billingID: billingId,
				language: getLanguage(),
			},
		});
	}, []);
	return (
		<GridContainer>
			<ConditionalRender
				show={show}
				onTrue={() => (
					<>
						<CustomDialog
							isOpen={billingNameDialog}
							hideDialog={hideUpdateAccountNameDialog}
							header={translate('payments.billingAccounts.name.edit.title')}
							dialogBody={
								<DialogBody
									billingId={billingId}
									value={newBillingName}
									setValue={(val) => setNewBillingName(val)}
									label={translate('payments.billingAccounts.billingAccountName')}
								/>
							}
							rightSideButtons={[
								{
									text: translate('cancel'),
									onClick: hideUpdateAccountNameDialog,
									color: 'tertiary-light',
								},
								{
									text: translate('save'),
									onClick: () => {
										setBillingNameDialog(false);
										updateBillingAccountInformation();
									},
									color: 'primary-light',
									disabled:
										newBillingName === billingAccountInformation?.billingAccountName ||
										newBillingName.length < 1,
								},
							]}
						/>
						<CustomDialog
							isOpen={recipientDialog}
							hideDialog={() => setRecipientDialog(false)}
							header={translate(`billing.account.${action}.recipient`)}
							dialogBody={
								<DialogBody
									billingId={billingId}
									value={recipientEmail}
									setValue={(val) => setRecipientEmail(val)}
									id="recipient-email-input"
									label={translate('payments.billingAccounts.recipientEmailAddress')}
								/>
							}
							leftSideButtons={
								[
									action === 'edit' && {
										text: translate('delete.without.number'),
										onClick: deleteRecipient,
										color: 'tertiary-light',
									},
								].filter(Boolean) as ISideButton[]
							}
							rightSideButtons={[
								{
									text: translate('cancel'),
									onClick: () => setRecipientDialog(false),
									color: 'tertiary-light',
								},
								{
									text: translate('save'),
									onClick: () => {
										setRecipientDialog(false);
										updateRecipients();
									},
									color: 'primary-light',
								},
							]}
						/>
						<BreadcrumbsItem to={`/dashboard/active-services/${billingId}/account-details`}>
							{`${billingId} - ${translate('path.account.details')}`}
						</BreadcrumbsItem>
						<BillingAccountDetailsHeader
							billingId={billingId}
							billingName={billingName}
							openDialog={() => setBillingNameDialog(true)}
						/>
						<BillingAccountInformation
							billingType={billingAccountInformation?.billingAccountType || ''}
							accountId={accountId}
							billingId={billingId}
							isEBill={isEBill}
							account={account}
						/>
						<EBills
							isEBill={isEBill}
							billingAccountInformation={billingAccountInformation}
							eBillRecipients={eBillRecipients}
							editRecipient={editRecipient}
							addRecipient={addRecipient}
						/>
					</>
				)}
			/>
		</GridContainer>
	);
};

export default BillingAccountDetails;
